import { useQuery } from "@tanstack/react-query"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import { useBoolVariation } from "@/hooks/launchDarklyHooks"
import {
  type AllProductTypes,
  isDoubleDouble,
} from "@/screens/loans/utils/LoanCapitalProducts"

/**
 * This hook currently manages post-close monthly payment data for a loan,
 * allowing a "mock" mode to add a second "B" loan item for double-double or banner on Zero Gravity.
 */
export function usePostCloseFeature(loanId: string) {
  const FCI_PAYMENT_URL = "https://www.myfci.com/Login"
  const GLS_PAYMENT_URL = "https://portal.glssolutions.us/"
  const BACKFLIP_PAYMENT_URL =
    "https://portal-login.willowservicing.com/u/login?state=hKFo2SBhcUNIbXJaM2dHVzZXZGZkWVgtc254RXZpWmFaZW9ubKFur3VuaXZlcnNhbC1sb2dpbqN0aWTZIGUwU3M0cldORXFCTkt1dk5TeFlwaWJjZ2xoRVlyQmFQo2NpZNkgUXpmOW9YMktmcVFmYW1lWWxnOEM0MWQyRkROUE5qM0k"

  const postCloseFeatureEnabled = useBoolVariation("post-close-feature", false)

  // Temporarily used to simulate various loan products
  const MOCK_DATA = false

  // Build the react-query options for the loan dashboard
  const queryOptions = loanDashboardQueryOptions(loanId)
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    ...queryOptions,
    select: (rawData) => {
      if (!rawData) {
        return {
          monthlyPayments: [],
          loanProduct: null,
          loanNumber: "",
          team: null,
          notificationsEnabled: false,
        }
      }

      const analysisId = rawData?.analysis?.id

      // Extract post-close info from rawData
      const monthlyPayments = rawData?.postClose?.monthlyPayments || []
      const loanProduct = rawData?.postClose?.loanInfo?.product || null
      const loanNumber = monthlyPayments[0]?.loanNumber ?? ""
      const team = rawData?.postClose?.team
      // Get notifications info from the analysis object on the dashboard data
      const notificationsEnabled =
        rawData?.analysis?.notificationsEnabled ?? false

      return {
        analysisId,
        monthlyPayments,
        loanProduct,
        loanNumber,
        team,
        notificationsEnabled,
      }
    },
  })

  // If the query returned nothing, short-circuit with defaults
  if (!data) {
    return {
      analysisId: null,
      postCloseFeatureEnabled,
      isLoading,
      isRefetching,
      error,
      // Return empty arrays / defaults to keep usage safe.
      monthlyPayments: [],
      loanNumber: "",
      loanProduct: null,
      team: null,
      notificationsEnabled: false,
      refetch,
      fciPaymentUrl: FCI_PAYMENT_URL,
      glsPaymentUrl: GLS_PAYMENT_URL,
      backflipPaymentUrl: BACKFLIP_PAYMENT_URL,
    }
  }

  let {
    analysisId,
    monthlyPayments,
    loanNumber,
    loanProduct,
    team,
    notificationsEnabled,
  } = data

  // ─────────────────────────────────────────────────────────────
  // HARDCODED SPOOF: Always simulate the double_double or zero_graity scenario
  // and ensure two monthly payment items.
  // ─────────────────────────────────────────────────────────────
  if (MOCK_DATA) {
    loanProduct = "0G" // "0G" // "double_double"
    // If we only have one monthly payment, add a second for the “B” loan
    if (monthlyPayments.length < 2) {
      monthlyPayments = [
        ...monthlyPayments,
        {
          loanNumber,
          monthlyPayment: 1234.56, // example mock payment
        },
      ]
    }
  }

  // If not actually a double_double, limit to a single monthly payment
  if (
    !isDoubleDouble(loanProduct as AllProductTypes) &&
    monthlyPayments.length > 0
  ) {
    monthlyPayments = [monthlyPayments[0]]
  }

  return {
    analysisId,
    postCloseFeatureEnabled,
    isLoading,
    isRefetching,
    error,
    monthlyPayments,
    loanNumber,
    loanProduct,
    team,
    notificationsEnabled,
    refetch,
    fciPaymentUrl: FCI_PAYMENT_URL,
    glsPaymentUrl: GLS_PAYMENT_URL,
    backflipPaymentUrl: BACKFLIP_PAYMENT_URL,
  }
}
